import Vue from 'vue'

// Legacy category ids, from before chipset ids became board part numbers. Kept because these
// short forms are what admins are used to reading in the tag list.
const LEGACY_CHIPSET_NAMES: Record<number, string> = {
  0: 'SW',
  24: 'TX2',
  25: 'NX/AGX',
  33: 'Nano'
}

export default Vue.extend({
  methods: {
    timeParser (timeString: string) {
      const a = new Date(parseInt(timeString) * 1000)
      const zeroPad = (num: number, places: number) =>
        String(num).padStart(places, '0')
      const months = [
        'Jan',
        'Feb',
        'Mar',
        'Apr',
        'May',
        'Jun',
        'Jul',
        'Aug',
        'Sep',
        'Oct',
        'Nov',
        'Dec'
      ]
      const year = a.getFullYear()
      const month = months[a.getMonth()]
      const date = a.getDate()
      const hour = zeroPad(a.getHours(), 2)
      const min = zeroPad(a.getMinutes(), 2)
      const sec = zeroPad(a.getSeconds(), 2)
      return date + ' ' + month + ' ' + year
    },

    calculateExpireTime (endTime: number) {
      const today = new Date().getTime()
      const diffTime = Math.abs(endTime - today)
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
      return diffDays
    },

    // "NVIDIA Jetson Orin Nano (Developer kit)" -> "Orin Nano (DevKit)"
    compactDeviceName (name: string) {
      return String(name || '')
        .replace(/^\s*NVIDIA\s+/i, '')
        .replace(/^\s*Jetson\s+/i, '')
        .replace(/^Server\s*-\s*Workstation$/i, 'Server')
        .replace(/Developer kit/i, 'DevKit')
        .replace(/\s*ram\s*\)/i, ')')
        .replace(/\s+/g, ' ')
        .trim()
    },

    /**
     * Label for a chipset id.
     *
     * `devices` is the category list from /admin/get-docker-sources (use `categories_all`, which
     * includes chipset 0). The previous version switched over four hardcoded ids and returned
     * undefined for everything else, so every Orin, Xavier NX and Thor chipset rendered as a
     * blank grey pill -- 394 tags showed a row of empty chips instead of their devices.
     *
     * Never returns undefined: an unknown id shows as the id itself, which is at least a thing
     * someone can look up.
     */
    shortenChipset (chipsetId: number, devices: Array<any> | null = null) {
      const match = (devices || []).find((d: any) => Number(d.chipset_id) === Number(chipsetId))
      if (match && match.name) {
        return (this as any).compactDeviceName(match.name)
      }
      const legacy = LEGACY_CHIPSET_NAMES[chipsetId]
      return legacy !== undefined ? legacy : String(chipsetId)
    },
    secondsToDhms (seconds: number) {
      // https://stackoverflow.com/questions/36098913/convert-seconds-to-days-hours-minutes-and-seconds
      seconds = Number(seconds)
      const d = Math.floor(seconds / (3600 * 24))
      const h = Math.floor((seconds % (3600 * 24)) / 3600)
      const m = Math.floor((seconds % 3600) / 60)
      const s = Math.floor(seconds % 60)

      const dDisplay = d > 0 ? d + (d === 1 ? ' day, ' : ' days, ') : ''
      const hDisplay = h > 0 ? h + (h === 1 ? ' hour, ' : ' hours, ') : ''
      const mDisplay = m > 0 ? m + (m === 1 ? ' minute, ' : ' minutes, ') : ''
      const sDisplay = s > 0 ? s + (s === 1 ? ' second' : ' seconds') : ''
      return dDisplay + hDisplay + mDisplay + sDisplay
    },

    modelChipsets (model: any, v = 'chipset_ids') {
      let chipsets = new Array<any>()
      for (const child in model.children) {
        if (Object.hasOwnProperty.call(model.children, child)) {
          const c = model.children[child]
          if (c[v]) chipsets = (new Array<any>()).concat(chipsets, c[v])
        }
      }
      if (chipsets.length === 0 && model.children.length === 0) {
        // model itself
        return model[v]
      }
      return [...new Set(chipsets)] // return uniques values only
    },

    timeParserDate (timeString: string, format = null) {
      const a = new Date(timeString)
      const zeroPad = (num: number, places: number) =>
        String(num).padStart(places, '0')
      const months = [
        'Jan',
        'Feb',
        'Mar',
        'Apr',
        'May',
        'Jun',
        'Jul',
        'Aug',
        'Sep',
        'Oct',
        'Nov',
        'Dec'
      ]
      const year = a.getFullYear()
      const month = months[a.getMonth()]
      const date = a.getDate()
      return date + ' ' + month + ' ' + year
    },
    formatBytes (bytes, decimals = 2) {
      if (!+bytes) return '0 Bytes'

      const k = 1000
      const dm = decimals < 0 ? 0 : decimals
      const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']

      const i = Math.floor(Math.log(bytes) / Math.log(k))

      return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`
    }
  }
})
